import { useEffect, useRef } from "react";
import { useAuth } from "../hooks/useAuth";

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || "";

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
            auto_select?: boolean;
            cancel_on_tap_outside?: boolean;
          }) => void;
          prompt: (notification?: (status: { isNotDisplayed: () => boolean; isSkippedMoment: () => boolean }) => void) => void;
          renderButton: (parent: HTMLElement, config: { theme?: string; size?: string; shape?: string; width?: number }) => void;
          revoke: (hint: string, callback?: () => void) => void;
        };
      };
    };
  }
}

let gsiLoaded = false;
let gsiInitialized = false;
let gsiLoadPromise: Promise<void> | null = null;

function loadGSI(): Promise<void> {
  if (gsiLoaded) return Promise.resolve();
  if (gsiLoadPromise) return gsiLoadPromise;
  gsiLoadPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.onload = () => { gsiLoaded = true; resolve(); };
    script.onerror = () => { gsiLoadPromise = null; reject(new Error("Failed to load Google Identity Services")); };
    document.head.appendChild(script);
  });
  return gsiLoadPromise;
}

/**
 * Loads GIS, shows One Tap prompt. If dismissed, the fallback Google
 * button can be rendered into a container via renderGoogleButton().
 */
export function GoogleOneTap() {
  const { user, loading, loginWithGoogle } = useAuth();
  const initialized = useRef(false);

  useEffect(() => {
    if (loading || user || !GOOGLE_CLIENT_ID || initialized.current) return;
    initialized.current = true;

    loadGSI().then(() => {
      if (!window.google) return;

      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: (response) => {
          loginWithGoogle(response.credential).catch(console.error);
        },
        auto_select: true,
        cancel_on_tap_outside: true,
      });
      gsiInitialized = true;

      // Show One Tap prompt
      window.google.accounts.id.prompt();
    }).catch(() => { /* GSI unavailable — login button will be non-functional */ });
  }, [loading, user, loginWithGoogle]);

  return null;
}

/**
 * Render the Google sign-in button into a container element.
 * Call this when the container becomes visible (e.g., dropdown opens).
 */
export function renderGoogleButton(container: HTMLElement): void {
  if (!gsiInitialized || !window.google) {
    // GIS not ready yet — try after a short delay
    loadGSI().then(() => {
      if (window.google && gsiInitialized) {
        window.google.accounts.id.renderButton(container, {
          theme: "outline",
          size: "medium",
          width: 200,
        });
      }
    }).catch(() => {});
    return;
  }
  window.google.accounts.id.renderButton(container, {
    theme: "outline",
    size: "medium",
    width: 200,
  });
}
